import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, TrendingUp, UserCircle, Download } from 'lucide-react';
import axios from 'axios';
import { printReportCard } from '../lib/printReportCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function gradeLabel(pct) {
  if (pct >= 90) return { label: 'A+', color: 'text-emerald-600' };
  if (pct >= 75) return { label: 'A', color: 'text-emerald-500' };
  if (pct >= 60) return { label: 'B', color: 'text-blue-500' };
  if (pct >= 45) return { label: 'C', color: 'text-amber-500' };
  return { label: 'D', color: 'text-red-500' };
}

export default function ParentReportCard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('parent_token');
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchChildren();
  }, []);

  useEffect(() => {
    if (selectedId) fetchReportCard(selectedId);
  }, [selectedId]);

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/parent/children`, { headers: { Authorization: `Bearer ${token}` } });
      const list = res.data.data || [];
      setStudents(list);
      if (list.length > 0) setSelectedId(list[0].student_id);
    } catch {
      toast.error('Failed to load children');
    }
  };

  const fetchReportCard = async (sid) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parent/student/${sid}/report-card`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data.data);
    } catch {
      toast.error('Failed to load report card');
    } finally {
      setLoading(false);
    }
  };

  const exams = data?.exams || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/parent/dashboard" className="text-slate-400 hover:text-slate-700 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-emerald-500" />
            <h1 className="font-bold text-slate-900 text-lg">Report Card</h1>
          </div>
          {data?.student && (
            <span className="text-sm text-slate-500">{data.student.name} · {data.student.class_name} {data.student.section}</span>
          )}
          {exams.length > 0 && (
            <button
              onClick={() => printReportCard(data)}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors print:hidden"
            >
              <Download className="h-4 w-4" /> Download PDF
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        {/* Student selector */}
        {students.length > 1 && (
          <div className="flex gap-3">
            {students.map(s => (
              <button
                key={s.student_id}
                onClick={() => setSelectedId(s.student_id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${selectedId === s.student_id ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`}
              >
                <UserCircle className="h-4 w-4" /> {s.full_name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        ) : exams.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400">No exam results available yet</p>
          </div>
        ) : (
          <div className="space-y-5">
            {exams.map((exam, i) => {
              const grade = gradeLabel(exam.overall_percentage);
              return (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  {/* Exam header */}
                  <div className="flex items-center justify-between px-5 py-4 bg-slate-50 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-500" />
                      <h3 className="font-semibold text-slate-900">{exam.exam_name}</h3>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-slate-500">{exam.total_obtained}/{exam.total_marks}</span>
                      <span className={`font-bold text-base ${grade.color}`}>{grade.label}</span>
                      <span className="text-slate-500">({exam.overall_percentage}%)</span>
                    </div>
                  </div>
                  {/* Subjects */}
                  <div className="divide-y divide-slate-100">
                    {exam.subjects.map((sub, j) => {
                      const sg = gradeLabel(sub.percentage);
                      return (
                        <div key={j} className="flex items-center justify-between px-5 py-3">
                          <span className="text-sm text-slate-700">{sub.subject}</span>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-slate-500">{sub.obtained}/{sub.max_marks}</span>
                            <span className={`font-semibold w-8 text-right ${sg.color}`}>{sg.label}</span>
                            <div className="w-20 bg-slate-100 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full bg-emerald-400" style={{ width: `${Math.min(sub.percentage, 100)}%` }} />
                            </div>
                            <span className="text-slate-500 w-10 text-right">{sub.percentage}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
